import React, { useState } from 'react';
import { useParams, generatePath, useLocation, createSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSearchParams } from 'src/utils/useSearchParams';
import { useNavigate } from 'src/utils/useNavigate';
import SelectField, { components, OptionProps } from 'src/components/SelectField';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import Text from 'src/components/Text';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { FORM_ERROR } from 'final-form';
import routes from 'src/constants/routes';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import { postAddBankAPI } from 'src/serverApi/banks';
import { formToQuery } from '../mappers';
import { SBER_BIC } from '../constants';
import ErrorDialog from '../ErrorDialog';
import SberAlert from './SberAlert';
import validate, { validateAccount, validateBik } from './validate';
import styles from './styles.module.scss';

const Option = (props: OptionProps<{ islMemberId: string }>) => {
  const [searchParams] = useSearchParams();
  const customization = searchParams.get('customization');

  if (customization && props.data.islMemberId?.toLowerCase() === customization.toLowerCase()) {
    return (
      <>
        <components.Option {...props} />
        <div className={styles['select-option-devider']} />
      </>
    );
  }

  return <components.Option {...props} />;
};

const formToAPI = (formData: { bik: { bic: string; name: string }; account: string }) => ({
  account: formData.account,
  bik: formData.bik.bic,
});

const inputMask = createNumberMask({
  integerLimit: 20,
  prefix: '',
  includeThousandsSeparator: false,
  allowLeadingZeroes: true,
});

type Props = {
  banks: { bic: string; name: string; islMemberId: string | undefined }[];
  organization: {
    name: string;
    inn: string;
    ogrn: string;
  };
};

const Form: React.FC<Props> = ({ banks, organization }) => {
  const { organizationUeid = '' } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorDialogData, setErrorDialogData] = useState<any>(null);
  const withDataManagementAuthorities = useWithAuthorities([permissions.dataManagement]);
  const navigate = useNavigate();

  const customization = searchParams.get('customization');
  const customizationBank = customization
    ? banks.find(bank => bank.islMemberId?.toLowerCase() === customization.toLowerCase())
    : undefined;

  const [currentIslMemberId, setCurrentIslMemberId] = useState<any>(customization);

  const options = customizationBank
    ? [
        customizationBank,
        ...banks.filter(bank => bank.islMemberId !== customizationBank.islMemberId),
      ].filter(Boolean)
    : banks;

  const handleCloseErrorDialog = () => {
    setErrorDialogData(null);
  };

  const handleSubmit = async (values: any) => {
    const returnUrlSearchParams = createSearchParams(formToQuery(values)).toString();

    try {
      setIsLoading(true);
      await withDataManagementAuthorities(async () => {
        const data = formToAPI(values);
        await postAddBankAPI(organizationUeid, data);
        toast.success('Банк успешно добавлен');
        navigate(generatePath(routes.organization, { organizationUeid }));
      }, `${location.pathname}?${returnUrlSearchParams}`);
    } catch (error) {
      const { type, params } = error as any;
      if (type === 'ORGANIZATION_NOT_ASSOCIATED_WITH_BANK') {
        setErrorDialogData(params);
      } else if (type === 'UNTRUSTED_ZONE_BANK') {
        toast.error(
          `Указанный банк ${values?.bik?.name} по БИК ${values?.bik?.bic} не находится в доверенной зоне. Для уточнения информации обратитесь в банк`
        );
      } else if (type === 'BANK_ALREADY_EXISTS') {
        toast.error('Банк уже добавлен');
      } else {
        toast.error('Произошла ошибка при попытке добавить банк');
      }
      return { [FORM_ERROR]: 'Произошла ошибка при попытке добавить банк' };
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate(generatePath(routes.organization, { organizationUeid }));
  };

  return (
    <>
      <FinalForm
        onSubmit={handleSubmit}
        submitting={isLoading}
        pristine={!searchParams.toString()}
        initialValues={{
          bik: searchParams.has('bik')
            ? JSON.parse(searchParams.get('bik') || '')
            : customizationBank,
          account: searchParams.has('account') ? searchParams.get('account') : undefined,
        }}
        validate={validate}
      >
        {({ submitting, pristine, valid }: { submitting: any; pristine: any; valid: any }) => {
          return (
            <div className={styles['form']}>
              <FinalField name="bik">
                {({ value, onChange, error }: { value: any; onChange: any; error: string }) => {
                  const valid = !validateBik(value);
                  return (
                    <>
                      {value?.bic === SBER_BIC && <SberAlert />}
                      <SelectField
                        value={value}
                        onChange={(value: any) => {
                          setCurrentIslMemberId(value?.islMemberId);
                          onChange(value);
                        }}
                        options={options}
                        getOptionValue={(option: any) => option.bic}
                        getOptionLabel={(option: any) => `${option.name} (${option.bic})`}
                        isSearchable
                        label="Банк"
                        placeholder="БИК головной организации или название банка"
                        error={error}
                        className={styles['input']}
                        components={{ Option }}
                      />
                      {valid && !value.partnerBank && (
                        <Text type="p" italic small className={styles['tip']}>
                          Выполнение операций через СБП с помощью токенов, которые были получены в
                          Агенте 1С:СБП, возможно только при наличии действующего{' '}
                          <a href="https://portal.1c.ru/applications/1C-SBP#prices" target="_blank">
                            платного тарифа
                          </a>
                          .
                        </Text>
                      )}
                    </>
                  );
                }}
              </FinalField>
              <FinalField name="account">
                {({ value, onChange, error }: { value: any; onChange: any; error: string }) => {
                  const valid = !validateAccount(value);
                  return (
                    <>
                      <InputField
                        label={
                          currentIslMemberId?.toLowerCase() === 'raiffeisen'
                            ? 'Расчетный/Транзитный счет'
                            : 'Расчетный счет'
                        }
                        value={value}
                        onChange={onChange}
                        mask={inputMask}
                        error={error}
                        className={styles['input']}
                      />
                      {valid && (
                        <Text type="p" italic small className={styles['tip']}>
                          Проверьте номер счета. Он будет использоваться для оплат через СБП
                        </Text>
                      )}
                    </>
                  );
                }}
              </FinalField>
              <Button
                disabled={submitting || pristine || !valid}
                width="available"
                type="submit"
                className={styles['button']}
              >
                Добавить
              </Button>
              <Button
                variant="outlined"
                width="available"
                onClick={handleCancelClick}
                className={styles['button']}
              >
                Отмена
              </Button>
            </div>
          );
        }}
      </FinalForm>

      <ErrorDialog
        isOpen={!!errorDialogData}
        onClose={handleCloseErrorDialog}
        organizationName={organization?.name}
        {...errorDialogData}
      />
    </>
  );
};

export default Form;
